import { useCallback, useMemo, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type { Sort } from '@/components/data-table/types'
import type { DateTimeRange } from '@/components/data-table/filters/date-range'
import { Card, CardContent } from '@/components/ui/card'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { inventoryHistoryApi } from '@/api/endpoints/inventory-history'
import { DataTable } from '@/components/data-table/data-table'
import {
  buildInventoryHistoryColumns,
  resolveOrderIdFromHistory,
} from '@/features/inventory/components/inventory-history-columns'
import {
  buildInventoryEventKindOptions,
  buildInventoryEventTypeOptions,
} from '@/features/inventory/components/inventory-history-options'
import { InventoryHistoryOrderDrawer } from '@/features/inventory/components/inventory-history-order-drawer'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

const DEFAULT_PAGE_SIZE = 50

export function InventoryHistoryPage() {
  const { t: tInventory } = useTranslation('inventory')
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  /**
   * Set when the page is reached from a booked delivery ("Zur Inventarhistorie"): it scopes the
   * whole timeline to that document's movement and sits above the user's own filters.
   */
  const { reference } = useSearch({ strict: false })

  const restaurantsById = useMemo(() => {
    return restaurants.reduce<Record<number, string>>((acc, r) => {
      acc[r.id] = r.name
      return acc
    }, {})
  }, [restaurants])

  const handleSelectOrder = useCallback((orderId: string) => {
    setSelectedOrderId(orderId)
  }, [])

  const columns = useMemo(
    () =>
      buildInventoryHistoryColumns(tInventory, restaurantsById, {
        onSelectOrder: handleSelectOrder,
      }),
    [restaurantsById, handleSelectOrder, tInventory],
  )

  const restaurantOptions = useMemo(
    () =>
      [
        { label: tInventory('timeline.filters.allRestaurants'), value: 'all' },
      ].concat(
        restaurants.map((r) => ({ label: r.name, value: String(r.id) })),
      ),
    [restaurants, tInventory],
  )

  const eventKindOptions = useMemo(
    () => buildInventoryEventKindOptions(tInventory),
    [tInventory],
  )

  const eventTypeOptions = useMemo(
    () => buildInventoryEventTypeOptions(tInventory),
    [tInventory],
  )

  async function fetchHistory({
    pageIndex,
    pageSize,
    sorting,
    filters,
    q,
  }: {
    pageIndex: number
    pageSize: number
    sorting?: Array<Sort>
    filters?: Record<string, unknown>
    q?: string
  }) {
    const restaurantFilter =
      typeof filters?.restaurantId === 'string'
        ? filters.restaurantId
        : undefined
    const restaurantId =
      restaurantFilter && restaurantFilter !== 'all'
        ? Number(restaurantFilter)
        : undefined
    const kindFilter =
      typeof filters?.eventKind === 'string' ? filters.eventKind : undefined
    const eventKind =
      kindFilter === 'movement' || kindFilter === 'reservation'
        ? kindFilter
        : 'all'
    const typeFilter =
      typeof filters?.eventType === 'string' ? filters.eventType : undefined
    const eventType =
      typeFilter && typeFilter !== 'all' ? typeFilter : undefined
    const sku =
      typeof filters?.sku === 'string' && filters.sku !== '' ? filters.sku : q
    const range = (filters?.dateRange as DateTimeRange | undefined) ?? undefined
    const fromDate = range?.from
    const toDate = range?.to ?? range?.from

    // Parse optional time values (HH:mm format)
    const parseTime = (time?: string) => {
      if (!time) return null
      const [hours, minutes] = time.split(':').map(Number)
      return { hours, minutes }
    }
    const fromTime = parseTime(range?.fromTime)
    const toTime = parseTime(range?.toTime)

    const fromIso = fromDate
      ? new Date(
          fromDate.getFullYear(),
          fromDate.getMonth(),
          fromDate.getDate(),
          fromTime?.hours ?? 0,
          fromTime?.minutes ?? 0,
          0,
          0,
        ).toISOString()
      : undefined
    const toIso = toDate
      ? new Date(
          toDate.getFullYear(),
          toDate.getMonth(),
          toDate.getDate(),
          toTime?.hours ?? 23,
          toTime?.minutes ?? 59,
          toTime ? 0 : 59, // Use :00 when specific time set, :59 for end of day
          toTime ? 0 : 999, // Use .000 when specific time set, .999 for end of day
        ).toISOString()
      : undefined

    // Extract sorting parameters
    const sortBy = sorting?.[0]?.id
    const sortDir = sorting?.[0]?.desc ? 'desc' : 'asc'

    const res = await inventoryHistoryApi.list({
      restaurantId,
      sku: sku || undefined,
      eventKind,
      eventType,
      reference,
      from: fromIso,
      to: toIso,
      page: pageIndex,
      pageSize,
      sortBy,
      sortDir: sortBy ? sortDir : undefined,
    })

    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / res.pageSize),
    }
  }

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div>
        <div className="mb-6">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/inventory">
                    {tInventory('timeline.backToInventory')}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {tInventory('timeline.pageTitle')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-2xl font-semibold">
            {tInventory('timeline.pageTitle')}
          </h1>
        </div>

        <Card>
          <CardContent className="pt-6">
            <DataTable
              queryKey={`inventory-history-${reference ?? 'all'}`}
              columns={columns}
              fetcher={fetchHistory}
              filters={[
                {
                  id: 'dateRange',
                  label: tInventory('timeline.filters.dateRange'),
                  type: 'date-range',
                  maxDate: new Date(),
                },
                {
                  id: 'restaurantId',
                  label: tInventory('timeline.filters.restaurant'),
                  type: 'select',
                  options: restaurantOptions,
                },
                {
                  id: 'eventKind',
                  label: tInventory('timeline.filters.kind'),
                  type: 'select',
                  options: eventKindOptions,
                },
                {
                  id: 'eventType',
                  label: tInventory('timeline.filters.type'),
                  type: 'select',
                  options: eventTypeOptions,
                },
                {
                  id: 'sku',
                  label: tInventory('timeline.filters.sku'),
                  type: 'text',
                  placeholder: tInventory('timeline.filters.skuPlaceholder'),
                },
              ]}
              globalSearch={false}
              selectable={false}
              enableColumnVisibility
              showDensityToggle={false}
              initialState={{
                pageSize: DEFAULT_PAGE_SIZE,
                columnVisibility: {
                  actor: false,
                  source: false,
                  reference: false,
                },
              }}
              getRowId={(row) => {
                if (row.movementId != null)
                  return `movement:${row.movementId}:${row.sku}`
                if (row.orderId != null)
                  return `reservation:${row.orderId}:${row.sku}:${row.eventType}:${row.createdAt}`
                return (
                  row.clientRef ||
                  `${row.eventKind}:${row.sku}:${row.createdAt}`
                )
              }}
              onRowClick={(row) => {
                const orderId = resolveOrderIdFromHistory(row.original)
                if (orderId) setSelectedOrderId(orderId)
              }}
            />
          </CardContent>
        </Card>

        <InventoryHistoryOrderDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          restaurantNameById={restaurantsById}
        />
      </div>
    </PermissionGate>
  )
}
